import { Divider, Dropdown, DropdownGroup, DropdownItem, FormGroup, InputGroup, InputGroupItem, MenuToggle, MenuToggleElement } from '@patternfly/react-core';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { CogIcon } from '@patternfly/react-icons';

import DebouncedTextInput from './debounced-text-input';
import ValidationHelperText from './validation-helper-text';

import './text-input-with-presets.css';
import { useTranslation } from 'react-i18next';

interface TextInputWithPresetsProps {
    fieldId: string,
    presetsTitle?: string,
    tooltip: string,
    onTextChange: (value: string) => void,
    getPresetIcon?: (preset: string) => React.ReactNode | null,
    defaultValue?: string | undefined,
    validated?: TextInputWithPresetsValidation,
    placeholder?: string,
    presets: string[],
    showFreeTextEntryPreset?: boolean,
    testId?: string,
}

export enum TextInputWithPresetsValidation { Default = 'default', Error = 'error' };

const TextInputWithPresets = (props: TextInputWithPresetsProps) => {
    const { fieldId, presetsTitle, tooltip, onTextChange, getPresetIcon, defaultValue, validated,
        placeholder, presets, showFreeTextEntryPreset, testId } = props;

    const { t } = useTranslation();

    const [isPresetToggleOpen, setIsPresetToggleOpen] = useState(false);

    const handleValueChange = useCallback((value: string) => {
        onTextChange(value);
    }, [onTextChange]);

    const inputRef = useRef<HTMLInputElement>(null);

    const handleSelectPreset = useCallback((preset: string, key?: string) => {
        handleValueChange(preset);
        if (key === 'FreeTextEntry') {
            // Set focus on the input for 'free text entry'
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }
        setIsPresetToggleOpen(false);
    }, [handleValueChange, inputRef]);

    const getIcon = useCallback((preset: string) => {
        if (getPresetIcon) {
            return getPresetIcon(preset);
        } else {
            return null;
        }
    }, [getPresetIcon]);

    // memoize dropdown presets
    const dropdownPresets = useMemo(() => {
        const listedPresets = presets.map(preset => {
            return (<DropdownItem
                key={preset}
                className='dicom-attr-defaults'
                onClick={() => handleSelectPreset(preset)}
                icon={getIcon(preset)}>
                {preset}
            </DropdownItem>);
        });

        if (showFreeTextEntryPreset) {
            listedPresets.push(
                <DropdownItem
                    key="FreeTextEntry"
                    className='dicom-attr-defaults'
                    onClick={() => handleSelectPreset('', "FreeTextEntry")}>
                    {t("selectionPage.dicomAttributePresets.freeTextEntry")}
                </DropdownItem>
            );
        }

        return listedPresets;
    },
        [getIcon, handleSelectPreset, presets, showFreeTextEntryPreset, t]);


    return (
        <InputGroup>
            <InputGroupItem className="input-with-presets-field-item">
                <DebouncedTextInput
                    fieldId={fieldId}
                    onDebouncedChange={handleValueChange}
                    className="input-field input-with-presets-field"
                    inputRef={inputRef}
                    defaultValue={defaultValue}
                    validated={validated}
                    placeholder={placeholder}
                    testId={testId}
                />
            </InputGroupItem>
            <InputGroupItem>
                <Dropdown
                    isPlain
                    isOpen={isPresetToggleOpen}
                    shouldFocusFirstItemOnOpen={false}
                    popperProps={{ position: 'right' }}
                    title={tooltip}
                    toggle={(toggleRef: React.Ref<MenuToggleElement>) =>
                        <MenuToggle
                            ref={toggleRef}
                            onClick={() => setIsPresetToggleOpen(!isPresetToggleOpen)}
                            variant='plain'
                            className='menutoggle-plain-select'>
                            <CogIcon />
                        </MenuToggle>}
                    onOpenChange={() => setIsPresetToggleOpen(!isPresetToggleOpen)}
                ><DropdownGroup label={presetsTitle} className='dropdown-group-label'><Divider />{dropdownPresets}</DropdownGroup></Dropdown>
            </InputGroupItem>
        </InputGroup>
    );
}

export default TextInputWithPresets;
